<template>
  <div
    class="size-full overflow-y-auto overflow-x-hidden sm:px-6 lg:px-8"
  >
    <div
      class="flex min-h-full flex-col justify-center py-12"
    >
      <div class="mx-auto max-w-sm">
        <BaseCard class="relative">
          <BaseInnerLoading
            v-if="isValidatingToken || isSubmitting"
          />
          <div class="space-y-4">
            <template v-if="!tokenValid">
              <div class="space-y-4 text-center">
                <IconExclamationTriangle
                  class="mx-auto size-12 text-red-500"
                />
                <p class="text-red-600">{{
                  errorMessage
                }}</p>
                <ButtonSecondary
                  @click="router.push({ name: 'login' })"
                >
                  Back to Login
                </ButtonSecondary>
              </div>
            </template>

            <template v-else>
              <h2 class="text-xl font-semibold">
                Reset Password
              </h2>
              <p v-if="userEmail" class="text-gray-600">
                Setting new password for
                <strong>{{ userEmail }}</strong>
              </p>

              <SetChangeResetPasswordForm
                :require-current-password="false"
                user-type="merchant-user"
                @submit="onSubmit"
              />
            </template>
          </div>
        </BaseCard>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SubmissionContext } from "vee-validate"
import { ApiError } from "@/api"
import type {
  SetChangeResetPasswordFormInput,
  SetChangeResetPasswordFormOutput,
} from "@/composables/forms/useSetChangeResetPasswordForm"

const route = useRoute()
const router = useRouter()

const isSubmitting = ref(false)
const isValidatingToken = ref(false)
const tokenValid = ref(true)
const errorMessage = ref("")
const token = route.query.token
const userEmail = ref<string | null>(null)

onMounted(async () => {
  if (!token || typeof token !== "string") {
    tokenValid.value = false
    errorMessage.value =
      "Invalid or missing password reset link"
    return
  }

  isValidatingToken.value = true
  try {
    userEmail.value = (
      await validatePasswordResetToken(token)
    ).email
    tokenValid.value = true
  } catch (error) {
    tokenValid.value = false
    if (error instanceof ApiError && error.code) {
      errorMessage.value =
        {
          "password-already-reset":
            "Please request a new reset password link",
          "token-expired":
            "This password reset link has expired. Please request a new one.",
          "invalid-token": "Invalid password reset link",
        }[error.code] || "Invalid password reset link"
    } else {
      errorMessage.value = "Invalid password reset link"
    }
  } finally {
    isValidatingToken.value = false
  }
})

const onSubmit = async (
  params: SetChangeResetPasswordFormOutput,
  context: SubmissionContext<SetChangeResetPasswordFormInput>
) => {
  if (!token || typeof token !== "string") return

  isSubmitting.value = true
  try {
    await resetPassword({
      token,
      password: params.password,
    })

    toast.success("Password reset successfully")
    router.push({ name: "login" })
  } catch (error) {
    if (error instanceof ApiError) {
      if (
        error.code &&
        [
          "invalid-new-password",
          "same-as-current-password",
        ].includes(error.code)
      ) {
        context.setFieldError(
          "password",
          error.detail ||
            "Password does not meet requirements"
        )
        return
      }
      if (
        error.code &&
        [
          "token-expired",
          "invalid-token",
          "password-already-reset",
        ].includes(error.code)
      ) {
        tokenValid.value = false
        errorMessage.value =
          {
            "password-already-reset":
              "Please request a new reset password link",
            "token-expired":
              "This password reset link has expired. Please request a new one.",
            "invalid-token": "Invalid password reset link",
          }[error.code] || "Invalid password reset link"
        return
      }
    }
    toast.error("Failed to reset password")
  } finally {
    isSubmitting.value = false
  }
}
</script>
